import { inject, Injectable } from '@angular/core';
import { Storage, ref, uploadBytes, getDownloadURL, uploadBytesResumable, UploadTaskSnapshot } from '@angular/fire/storage';
import { from, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class StorageService {
    private readonly storage = inject(Storage);

    /**
     * Uploads a file to a specific path in Firebase Storage and returns the download URL.
     * @param path The path where the file should be stored (e.g., 'chat/images/filename.jpg')
     * @param file The File or Blob object to upload
     */
    uploadFile(path: string, file: Blob | File): Observable<string> {
        const storageRef = ref(this.storage, path);
        return from(uploadBytes(storageRef, file)).pipe(
            switchMap(() => from(getDownloadURL(storageRef)))
        );
    }

    /**
     * Uploads a file with progress tracking.
     */
    uploadFileWithProgress(path: string, file: Blob | File): { task: Observable<UploadTaskSnapshot>, url: Observable<string> } {
        const storageRef = ref(this.storage, path);
        const uploadTask = uploadBytesResumable(storageRef, file);

        const task$ = new Observable<UploadTaskSnapshot>(subscriber => {
            uploadTask.on('state_changed',
                snapshot => subscriber.next(snapshot),
                error => subscriber.error(error),
                () => subscriber.complete()
            );
        });

        const url$ = from(uploadTask.then()).pipe(
            switchMap(() => from(getDownloadURL(storageRef)))
        );

        return {
            task: task$,
            url: url$
        };
    }
}
